// ============================================================================
// Centralized Route Definitions
// Single source of truth for both AppRouter and Voice Navigation
// ============================================================================

export interface VoiceRouteDefinition {
  path: string;
  label: string;
  keywords: string[];
  category: RouteCategory;
  requiresPatientId?: boolean;
  patientPath?: string; // Path to use when patient is selected (with :id placeholder)
  fallbackPath?: string; // Path to use when no patient is selected
}

export type RouteCategory = 
  | 'admin'
  | 'clinical'
  | 'lab'
  | 'billing'
  | 'inventory'
  | 'configuration'
  | 'ai'
  | 'reports';

// ============================================================================
// All navigable routes with voice keywords
// This is the single source of truth - update here to reflect in voice nav
// ============================================================================

export const VOICE_ROUTES: VoiceRouteDefinition[] = [
  // =========================================================================
  // ADMIN ROUTES
  // =========================================================================
  {
    path: '/admin/appointments',
    label: 'Appointments',
    keywords: ['appointments', 'calendar', 'schedule', 'booking', 'appointment','admin'],
    category: 'admin',
  },
  {
    path: '/admin/appointments/patient/:id',
    label: 'Patient Appointments',
    keywords: ['patient appointments', 'patient calendar', 'patient schedule'],
    category: 'admin',
    requiresPatientId: true,
    fallbackPath: '/admin/appointments',
  },
  {
    path: '/admin/registration/patient-list',
    label: 'Patient List',
    keywords: ['patient list', 'patients', 'all patients', 'view patients', 'registration list'],
    category: 'admin',
  },
  {
    path: '/admin/registration/new-registration',
    label: 'New Registration',
    keywords: ['new registration', 'register patient', 'new patient', 'add patient', 'create patient'],
    category: 'admin',
  },
  {
    path: '/admin/registration/patient/:id',
    label: 'Patient View',
    keywords: ['patient view', 'view patient', 'patient details', 'patient profile'],
    category: 'admin',
    requiresPatientId: true,
    fallbackPath: '/admin/registration/patient-list',
  },
  {
    path: '/admin/registration/patient-partner-registration',
    label: 'Patient Partner Registration',
    keywords: ['partner registration', 'add partner', 'register partner', 'partner'],
    category: 'admin',
  },
  {
    path: '/admin/registration/billing-information',
    label: 'Billing Information',
    keywords: ['billing information', 'billing info', 'patient billing'],
    category: 'admin',
  },
  {
    path: '/admin/cima/patient-list',
    label: 'CIMA',
    keywords: ['cima', 'cima patients', 'sema', 'seema'],
    category: 'admin',
  },
  {
    path: '/admin/refera/patient-list',
    label: 'Refera MD',
    keywords: ['refera', 'refera md', 'referral', 'refer md', 'referee'],
    category: 'admin',
  },
  {
    path: '/admin/vidai-leads/patient-list',
    label: 'Vidai Leads',
    keywords: ['vidai leads', 'leads', 'patient leads', 'video leads'],
    category: 'admin',
  },

  // =========================================================================
  // BILLING ROUTES
  // =========================================================================
  {
    path: '/admin/billing/package',
    label: 'Billing Package',
    keywords: ['billing', 'billing package', 'package', 'packages', 'billing packages'],
    category: 'billing',
  },
  {
    path: '/admin/billing/package-details/:id',
    label: 'Package Details',
    keywords: ['package details', 'view package', 'package info'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/package',
  },
  {
    path: '/admin/billing/invoice',
    label: 'Invoice',
    keywords: ['invoice', 'invoices', 'billing invoice', 'invoice list'],
    category: 'billing',
  },
  {
    path: '/admin/billing/invoice/new',
    label: 'New Invoice',
    keywords: ['new invoice', 'create invoice', 'add invoice'],
    category: 'billing',
  },
  {
    path: '/admin/billing/invoice/new/:id',
    label: 'New Patient Invoice',
    keywords: ['patient invoice', 'create patient invoice'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/invoice/new',
  },
  {
    path: '/admin/billing/invoice/view/:id',
    label: 'View Invoice',
    keywords: ['view invoice', 'invoice details', 'invoice view'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/invoice',
  },
  {
    path: '/admin/billing/invoice/log/:id',
    label: 'Invoice Logs',
    keywords: ['invoice logs', 'invoice history', 'invoice log'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/invoice',
  },
  {
    path: '/admin/billing/pharmacy',
    label: 'Pharmacy',
    keywords: ['pharmacy', 'pharmacy billing', 'medicine', 'medicines', 'drugs', 'farmacy'],
    category: 'billing',
  },
  {
    path: '/admin/billing/pharmacy/new',
    label: 'New Pharmacy Invoice',
    keywords: ['new pharmacy', 'create pharmacy invoice', 'pharmacy new'],
    category: 'billing',
  },
  {
    path: '/admin/billing/pharmacy/new/:id',
    label: 'New Patient Pharmacy',
    keywords: ['patient pharmacy', 'pharmacy patient'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/pharmacy/new',
  },
  {
    path: '/admin/billing/pharmacy/view/:id',
    label: 'View Pharmacy Invoice',
    keywords: ['view pharmacy', 'pharmacy details', 'pharmacy invoice view'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/pharmacy',
  },
  {
    path: '/admin/billing/pharmacy/logs/:id',
    label: 'Pharmacy Logs',
    keywords: ['pharmacy logs', 'pharmacy history'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/pharmacy',
  },
  {
    path: '/admin/billing/advance/advance-list',
    label: 'Advance',
    keywords: ['advance', 'advance list', 'advance payment', 'collect advance'],
    category: 'billing',
  },
  {
    path: '/admin/billing/advance/collect-advance/:id',
    label: 'Collect Advance',
    keywords: ['collect advance', 'add advance', 'advance collection'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/advance/advance-list',
  },
  {
    path: '/admin/billing/advance/request-refund/:id',
    label: 'Request Refund',
    keywords: ['request refund', 'advance refund', 'refund request'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/advance/advance-list',
  },
  {
    path: '/admin/billing/advance/advance-logs/:id',
    label: 'Advance Logs',
    keywords: ['advance logs', 'advance history'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/advance/advance-list',
  },
  {
    path: '/admin/billing/refund/refund-list',
    label: 'Refund',
    keywords: ['refund', 'refund list', 'refunds', 'request refund'],
    category: 'billing',
  },
  {
    path: '/admin/billing/refund/refund-details/:id',
    label: 'Refund Details',
    keywords: ['refund details', 'view refund', 'refund info'],
    category: 'billing',
    requiresPatientId: true,
    fallbackPath: '/admin/billing/refund/refund-list',
  },

  // =========================================================================
  // INVENTORY ROUTES
  // =========================================================================
  {
    path: '/admin/inventory/item-management/stock',
    label: 'Available Stock',
    keywords: ['stock', 'available stock', 'inventory', 'inventory stock'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/item-management/material-consumption-logs',
    label: 'Material Consumption Logs',
    keywords: ['material consumption', 'consumption logs', 'material logs'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/stock-transfer/indent',
    label: 'Indent',
    keywords: ['indent', 'stock indent', 'indent list', 'stock transfer indent', 'stock transfer'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/stock-transfer/issue',
    label: 'Stock Issue',
    keywords: ['stock issue', 'issue', 'issue list', 'stock transfer issue'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/stock-transfer/received',
    label: 'Stock Received',
    keywords: ['stock received', 'received', 'received list'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/purchase/requisition',
    label: 'Purchase Requisition',
    keywords: ['requisition', 'purchase requisition', 'pr', 'requisition list'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/purchase/requisition/new',
    label: 'New Requisition',
    keywords: ['new requisition', 'create requisition', 'add requisition'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/purchase/order/prospective',
    label: 'Purchase Order Prospective',
    keywords: ['purchase order', 'po', 'prospective orders', 'prospective po'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/purchase/order/ordered',
    label: 'Purchase Order Ordered',
    keywords: ['ordered', 'ordered list', 'po ordered'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/purchase/order/new',
    label: 'New Purchase Order',
    keywords: ['new purchase order', 'create po', 'new po'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/grn/prospective',
    label: 'GRN Prospective',
    keywords: ['grn', 'grn prospective', 'goods received note'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/grn/received',
    label: 'GRN Received',
    keywords: ['grn received', 'received grn', 'goods received'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/return/grn',
    label: 'Return GRN',
    keywords: ['return grn', 'grn return', 'return goods'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/return/issue',
    label: 'Return Issue',
    keywords: ['return issue', 'issue return'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/return/receive',
    label: 'Return Receive',
    keywords: ['return receive', 'receive return'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/return/expired',
    label: 'Expired Stock',
    keywords: ['expired', 'expired stock', 'expired items'],
    category: 'inventory',
  },
  {
    path: '/admin/inventory/return/pharmacy',
    label: 'Return Pharmacy',
    keywords: ['return pharmacy', 'pharmacy return'],
    category: 'inventory',
  },

  // =========================================================================
  // CLINICAL ROUTES
  // =========================================================================
  {
    path: '/clinical/dashboard',
    label: 'Clinical Dashboard',
    keywords: ['clinical dashboard', 'clinical', 'clinic dashboard'],
    category: 'clinical',
  },
  {
    path: '/clinical/patients',
    label: 'Clinical Patients',
    keywords: ['clinical patients', 'clinical patient list', 'clinic patients', 'patient list clinical'],
    category: 'clinical',
  },
  {
    path: '/clinical/patients/Summary/:id',
    label: 'Patient Summary',
    keywords: ['summary', 'patient summary', 'overview', 'clinical summary'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/medical-history/:id',
    label: 'Medical History',
    keywords: ['medical history', 'history', 'patient history'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/examination/:id',
    label: 'Examination',
    keywords: ['examination', 'exam', 'physical exam'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/hpi/:id',
    label: 'HPI',
    keywords: ['hpi', 'history of present illness'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/investigation/:id',
    label: 'Investigation',
    keywords: ['investigation', 'investigations', 'tests', 'lab tests'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/stimulation/:id',
    label: 'Stimulation',
    keywords: ['stimulation', 'stim', 'ovarian stimulation'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/cycle-overview/:id',
    label: 'Cycle Overview',
    keywords: ['cycle overview', 'cycle', 'ivf cycle'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/treatment-plan/:id',
    label: 'Treatment Plan',
    keywords: ['treatment plan', 'treatment', 'plan'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/treatment-plan/:id?view=drug_list',
    label: 'Drug List',
    keywords: ['drug list', 'drugs', 'medications', 'medicine list', 'prescription', 'prescriptions'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/surgery/:id',
    label: 'Surgery',
    keywords: ['surgery', 'surgical', 'operation'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/outcome/:id',
    label: 'Outcome',
    keywords: ['outcome', 'outcomes', 'results'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },
  {
    path: '/clinical/patients/document/:id',
    label: 'Documents',
    keywords: ['documents', 'document', 'files', 'patient documents'],
    category: 'clinical',
    requiresPatientId: true,
    fallbackPath: '/clinical/patients',
  },

  // =========================================================================
  // LAB ROUTES
  // =========================================================================
  {
    path: '/lab/dashboard',
    label: 'Lab Dashboard',
    keywords: ['lab dashboard', 'laboratory dashboard'],
    category: 'lab',
  },
  {
    path: '/lab/embryology',
    label: 'Embryology',
    keywords: ['embryology', 'embryo', 'embryology patients', 'embryo patient list'],
    category: 'lab',
  },
  {
    path: '/lab/embryology/oocyte-grading/:id',
    label: 'Oocyte Grading',
    keywords: ['oocyte grading', 'oocyte', 'egg grading', 'oocyte quality'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/embryology/embryo-culture/:id',
    label: 'Embryo Culture',
    keywords: ['embryo culture', 'culture', 'embryo development'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/embryology/ai-embryo/:id',
    label: 'AI Embryo',
    keywords: ['ai embryo', 'ai grading', 'embryo ai'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/embryology/pgta/:id',
    label: 'PGT',
    keywords: ['pgt', 'pgta', 'pgt-a', 'preimplantation genetic testing', 'genetic testing'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/embryology/pgta2/:id',
    label: 'PGT V2',
    keywords: ['pgt v2', 'pgta2', 'pgt version 2'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/embryology/cryo-thawing/:id',
    label: 'Cryo Thawing',
    keywords: ['cryo thawing', 'thawing', 'embryo thawing', 'cryo thaw'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/embryology',
  },
  {
    path: '/lab/andrology',
    label: 'Andrology',
    keywords: ['andrology', 'andrology patients', 'semen', 'sperm analysis'],
    category: 'lab',
  },
  {
    path: '/lab/andrology/semen-analysis/:id',
    label: 'Semen Analysis',
    keywords: ['semen analysis', 'sperm analysis', 'semen test', 'sperm test', 'sa'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/andrology',
  },
  {
    path: '/lab/andrology/semen-processing/:id',
    label: 'Semen Processing',
    keywords: ['semen processing', 'sperm processing', 'processing'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/andrology',
  },
  {
    path: '/lab/andrology/semen-freezing/:id',
    label: 'Semen Freezing',
    keywords: ['semen freezing', 'sperm freezing', 'sperm freeze'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/andrology',
  },
  {
    path: '/lab/andrology/semen-thawing/:id',
    label: 'Semen Thawing',
    keywords: ['semen thawing', 'sperm thawing', 'sperm thaw'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/andrology',
  },
  {
    path: '/lab/andrology/IUI/:id',
    label: 'IUI',
    keywords: ['iui', 'intrauterine insemination', 'insemination'],
    category: 'lab',
    requiresPatientId: true,
    fallbackPath: '/lab/andrology',
  },
  {
    path: '/lab/RFID/dashboard',
    label: 'RFID Dashboard',
    keywords: ['rfid', 'rfid dashboard', 'audit', 'audity'],
    category: 'lab',
  },
  {
    path: '/lab/RFID/patient-list',
    label: 'RFID Patient List',
    keywords: ['rfid patients', 'rfid patient list'],
    category: 'lab',
  },
  {
    path: '/lab/cryostorage',
    label: 'Cryo Storage',
    keywords: ['cryo storage', 'cryostorage', 'cryo', 'storage'],
    category: 'lab',
  },
  {
    path: '/lab/cryostorage/oocyte',
    label: 'Oocyte Cryo Storage',
    keywords: ['oocyte', 'oocyte storage', 'oocyte cryo', 'egg storage', 'egg freezing'],
    category: 'lab',
  },
  {
    path: '/lab/cryostorage/embryo',
    label: 'Embryo Storage',
    keywords: ['embryo storage', 'embryo cryo', 'frozen embryo'],
    category: 'lab',
  },
  {
    path: '/lab/cryostorage/sperm',
    label: 'Sperm Storage',
    keywords: ['sperm storage', 'sperm cryo', 'frozen sperm', 'sperm freezing'],
    category: 'lab',
  },

  // =========================================================================
  // AI ROUTES 
  // =========================================================================
  {
    path: '/ai/aiHub',
    label: 'AI Hub',
    keywords: ['ai hub', 'ai', 'artificial intelligence', 'ai home'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/embryograderlist',
    label: 'AI Embryo Grader List',
    keywords: ['embryo grader', 'ai embryo', 'embryo grading list', 'ai grader list'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/embryograder',
    label: 'AI Embryo Grader',
    keywords: ['embryo grader', 'ai embryo grader', 'grade embryo', 'ai grading'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/aileadchatbot',
    label: 'AI Lead Chatbot',
    keywords: ['ai chatbot', 'lead chatbot', 'ai lead', 'chatbot'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/aileadchatbot/chat',
    label: 'AI Lead Chat',
    keywords: ['ai lead chat', 'lead chat', 'chatbot conversation'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/voicedataentry',
    label: 'AI Voice Data Entry',
    keywords: ['voice data entry', 'voice entry', 'ai voice', 'voice data'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/voicedataentry/chat',
    label: 'AI Voice Chat',
    keywords: ['voice chat', 'ai voice chat', 'voice chatbot'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/livebirthprediction',
    label: 'Live Birth Prediction',
    keywords: ['live birth', 'birth prediction', 'live birth prediction', 'prediction'],
    category: 'ai',
  },
  {
    path: '/ai/aiHub/livebirthprediction/:patientId',
    label: 'Live Birth Prediction Detail',
    keywords: ['live birth detail', 'birth prediction detail', 'patient prediction'],
    category: 'ai',
    requiresPatientId: true,
    fallbackPath: '/ai/aiHub/livebirthprediction',
  },
  {
    path: '/ai/Voicedataentry',
    label: 'Voice Data Entry',
    keywords: ['voice data', 'voice entry', 'data entry voice'],
    category: 'ai',
  },

  // =========================================================================
  // CONFIGURATION ROUTES
  // =========================================================================
  {
    path: '/ai/configuration/user',
    label: 'User Configuration',
    keywords: ['user config', 'user configuration', 'users', 'user settings', 'user management'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/userDetails',
    label: 'User Details',
    keywords: ['user details', 'add user', 'edit user', 'user info'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/consent',
    label: 'Consent Configuration',
    keywords: ['consent', 'consent config', 'consent configuration', 'consent list'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/consent/new',
    label: 'New Consent',
    keywords: ['new consent', 'add consent', 'create consent'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/clinic',
    label: 'Clinic Configuration',
    keywords: ['clinic config', 'clinic configuration', 'clinic settings', 'clinic list'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/page',
    label: 'Page Configuration',
    keywords: ['page config', 'page configuration', 'page settings'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/package-configuration/package',
    label: 'Package Configuration',
    keywords: ['package config', 'package configuration', 'configure packages', 'package list'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/package-configuration/new',
    label: 'New Package Configuration',
    keywords: ['new package config', 'add package', 'create package'],
    category: 'configuration',
  },
  {
    path: '/ai/configuration/package-configuration/package-Definition',
    label: 'Package Definition',
    keywords: ['package definition', 'define package', 'package setup'],
    category: 'configuration',
  },

  // =========================================================================
  // REPORTS ROUTES
  // =========================================================================
  {
    path: '/ai/reports/clinical',
    label: 'Clinical Reports',
    keywords: ['clinical reports', 'reports', 'admin reports', 'mis reports'],
    category: 'reports',
  },
  {
    path: '/ai/reports/clinical/template1',
    label: 'Report Template',
    keywords: ['report template', 'template1', 'report table'],
    category: 'reports',
  },

  // =========================================================================
  // KPI DASHBOARD
  // =========================================================================
  {
    path: '/ai/kpi_dashboard',
    label: 'KPI Dashboard',
    keywords: ['kpi', 'kpi dashboard', 'key performance', 'analytics', 'dashboard analytics'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/bar_chart',
    label: 'Bar Chart',
    keywords: ['bar chart', 'bar graph', 'kpi bar'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/pie_chart',
    label: 'Pie Chart',
    keywords: ['pie chart', 'pie graph', 'kpi pie'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/polar_area_chart',
    label: 'Polar Area Chart',
    keywords: ['polar area', 'polar chart', 'area chart'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/line_chart',
    label: 'Line Chart',
    keywords: ['line chart', 'line graph', 'kpi line'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/radar_chart',
    label: 'Radar Chart',
    keywords: ['radar chart', 'radar graph', 'spider chart'],
    category: 'reports',
  },
  {
    path: '/ai/kpi_dashboard/donut_chart',
    label: 'Donut Chart',
    keywords: ['donut chart', 'donut graph', 'doughnut chart'],
    category: 'reports',
  },
];

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Get all routes for a specific category
 */
export function getRoutesByCategory(category: RouteCategory): VoiceRouteDefinition[] {
  return VOICE_ROUTES.filter(route => route.category === category);
}

/**
 * Get route by exact path
 */
export function getRouteByPath(path: string): VoiceRouteDefinition | undefined {
  return VOICE_ROUTES.find(route => route.path === path);
}

/**
 * Get all unique categories
 */
export function getAllCategories(): RouteCategory[] {
  return Array.from(new Set(VOICE_ROUTES.map(route => route.category)));
}

/**
 * Generate flat list of all keywords with their routes
 */
export function getKeywordRouteMap(): Map<string, VoiceRouteDefinition> {
  const map = new Map<string, VoiceRouteDefinition>();
  VOICE_ROUTES.forEach(route => {
    route.keywords.forEach(keyword => {
      map.set(keyword.toLowerCase(), route);
    });
  });
  return map;
}
